import { Observable } from 'rxjs';
import { ComponentStore } from '@ngrx/component-store';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { State } from '@progress/kendo-data-query';

import { EntityListDataState } from './model';

/**
 * Base class for entity list stores that provides common functionality
 * for managing grid data, loading states, and data state persistence.
 */
export abstract class BaseEntityListStore<T, TState extends EntityListDataState<T> = EntityListDataState<T>> 
    extends ComponentStore<TState> {
    
    // Static storage for dataState per entity type - shared across all store types
    protected static entityDataStates: Map<string, State> = new Map();

    // Common observables
    readonly gridData$: Observable<GridDataResult> = this.select((s) => s.gridData);
    readonly loading$: Observable<boolean> = this.select((s) => s.loading);
    readonly error$: Observable<string | undefined> = this.select((s) => s.error);
    readonly dataState$: Observable<State> = this.select((s) => s.dataState);

    constructor(
        protected entityType: string,
        initialState: TState
    ) {
        // Get existing dataState for this entity type or create a default one
        const existingDataState = BaseEntityListStore.entityDataStates.get(entityType) || { skip: 0, take: 10 };
        
        super({
            ...initialState,
            dataState: existingDataState,
            entityType: entityType
        } as TState);
    }

    /**
     * Updates the data state and persists it for this entity type
     */
    readonly setDataState = this.updater((state, dataState: State) => {
        // Persist the dataState for this entity type
        BaseEntityListStore.entityDataStates.set(state.entityType, dataState);
        return { ...state, dataState } as TState;
    });

    /**
     * Clears the stored dataState for this entity type
     */
    clearDataState(): void {
        const defaultDataState = { skip: 0, take: 10 };
        BaseEntityListStore.entityDataStates.set(this.entityType, defaultDataState);
        this.patchState({ dataState: defaultDataState } as Partial<TState>);
    }

    /**
     * Static method to clear all stored dataStates
     */
    static clearAllDataStates(): void {
        BaseEntityListStore.entityDataStates.clear();
    }

    /**
     * Gets the current data state for this entity type
     */
    protected getCurrentDataState(): State {
        return BaseEntityListStore.entityDataStates.get(this.entityType) || { skip: 0, take: 10 };
    }

    /**
     * Updates loading state
     */
    protected setLoading(loading: boolean): void {
        this.patchState({ loading } as Partial<TState>);
    }

    /**
     * Updates error state
     */
    protected setError(error?: string): void {
        this.patchState({ error } as Partial<TState>);
    }

    /**
     * Updates grid data
     */
    protected setGridData(gridData: GridDataResult): void {
        this.patchState({ gridData } as Partial<TState>);
    }

    /**
     * Abstract method that subclasses must implement to define their data loading strategy
     */
    abstract loadData(): void;
}